import { useState, useEffect } from "react";
import { useBranding } from "../../../../context/BrandingContext";
import { useUserEmail } from "../../../../context/UserEmailContext";
import { useCfmContext } from "../Context/CfmContext";
import { storage, auth } from "../../../../backend/firebaseconfig";
import { ref, getDownloadURL } from "firebase/storage";

/**
 * Custom React hook to retrieve and manage client-specific metadata and branding information.
 *
 * This hook determines if the current user owns the client, and provides branding values
 * such as trading name, primary color, logo, regulatory text, and user name. If the client
 * is not owned by the current user, it attempts to fetch the appropriate logo from storage.
 *
 * @returns {Object} An object containing:
 *   @property {boolean} isOwnClient - Indicates if the current user owns the client.
 *   @property {string} tradingName - The trading name for the client.
 *   @property {string} primaryColor - The primary color for the client's branding.
 *   @property {string} logo - The URL or data for the client's logo.
 *   @property {string} userName - The display name or email of the user.
 *   @property {string} regulatoryText - The regulatory text for the client.
 */
export const useClientMeta = () => {
  const { state } = useCfmContext();
  const { userEmail } = useUserEmail();
  const {
    tradingName: defaultTradingName,
    primaryColor: defaultPrimaryColor,
    logo: defaultLogo,
    regulatoryText: defaultRegulatoryText,
  } = useBranding();

  //  Determine ownership
  const isOwnClient = state?.createdBy ? state.createdBy === userEmail : true;

  //  Branding values
  const tradingName = isOwnClient
    ? defaultTradingName
    : state.tradingName || defaultTradingName;

  const primaryColor = isOwnClient
    ? defaultPrimaryColor
    : state.primaryColor || defaultPrimaryColor;

  const regulatoryText = isOwnClient
    ? defaultRegulatoryText
    : state.regulatoryText || defaultRegulatoryText;

  //  Dynamic logo (fetch from storage if other's client)
  const [logo, setLogo] = useState(defaultLogo);

  useEffect(() => {
    if (isOwnClient) {
      setLogo(defaultLogo);
      return;
    }

    /**
     * Asynchronously fetches the logo image URL for the current trading name from Firebase storage.
     * If the trading name is not set, or if the logo cannot be found, sets the logo state to an empty string.
     *
     * @async
     * @function fetchLogo
     * @returns {Promise<void>} Resolves when the logo URL is fetched and state is updated.
     *
     * @throws Will not throw, but will set logo to an empty string if fetching fails.
     */
    const fetchLogo = async () => {
      if (!state.tradingName) {
        setLogo("");
        return;
      }
      try {
        const logoRef = ref(storage, `Logos/${state.tradingName}.jpg`);
        const logoURL = await getDownloadURL(logoRef);
        setLogo(logoURL);
      } catch {
        setLogo("");
      }
    };

    fetchLogo();
  }, [isOwnClient, state.tradingName, defaultLogo]);

  // Smart userName
  const userName = isOwnClient
    ? auth.currentUser?.displayName || userEmail
    : state.createdBy || userEmail;

  // console.log(state.regulatoryText);

  return {
    isOwnClient,
    tradingName,
    primaryColor,
    logo,
    userName,
    regulatoryText,
  };
};
